import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, HelpCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

import Layout from "../components/Layout";
import { api, currency } from "../lib/api";
import {
  GYM_TYPES,
  MANAGER_INCLUDED,
  FOUR_WEEK_OPTIONS,
  FINANCIAL_RECORDS,
  LEASE_TENURE,
  EQUIPMENT_LEVELS,
  EQUIPMENT_CONDITIONS,
  WORKING_OWNERS,
  STATES,
  SELLING_TIMEFRAMES,
} from "../lib/constants";
import { Progress } from "../components/ui/progress";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";

const STEPS = [
  "Gym type",
  "Members",
  "Revenue & Profit",
  "Owner involvement",
  "Team & Management",
  "Business quality",
  "Equipment",
  "Your details",
];

const emptyInputs = {
  gym_type: "",
  active_members: "",
  member_change: "",
  revenue: "",
  net_profit: "",
  owner_payments: "",
  owner_hours: "",
  working_owners: 1,
  additional_owner_hours: "",
  employees: "",
  manager_included: "",
  operates_without_owner_4wk: "",
  financial_records: "",
  lease_tenure: "",
  lease_landlord_offer: "",  // 'yes' | 'no' — only relevant when lease_tenure === 'under_1_or_uncertain'
  owner_is_main_trainer: false,
  // ---- Equipment module ----
  equipment_original_cost: "",
  equipment_purchase_date: "",
  equipment_level: "",
  equipment_condition: "",
  expected_equipment_capex_2_years: "",
};

const emptyLead = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  business_name: "",
  state: "",
  selling_timeframe: "",
};

function LabelWithTip({ label, tip, testid }) {
  return (
    <div className="mb-2 flex items-center gap-2">
      <Label className="font-medium text-slate-800">{label}</Label>
      {tip && (
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" data-testid={testid} className="text-slate-400 hover:text-slate-600">
                <HelpCircle className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">{tip}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}

export default function Questionnaire() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [values, setValues] = useState(emptyInputs);
  const [lead, setLead] = useState(emptyLead);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const setV = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));
  const setL = (k, v) => setLead((prev) => ({ ...prev, [k]: v }));

  const progress = ((step + 1) / STEPS.length) * 100;

  const parseInputs = () => ({
    gym_type: values.gym_type,
    active_members: Number(values.active_members || 0),
    member_change: Number(values.member_change || 0),
    revenue: Number(values.revenue || 0),
    net_profit: Number(values.net_profit || 0),
    owner_payments: Number(values.owner_payments || 0),
    owner_hours: Number(values.owner_hours || 0),
    working_owners: Number(values.working_owners || 1),
    additional_owner_hours: Number(values.additional_owner_hours || 0),
    employees: Number(values.employees || 0),
    manager_included: values.manager_included,
    operates_without_owner_4wk: values.operates_without_owner_4wk,
    financial_records: values.financial_records,
    lease_tenure: values.lease_tenure,
    lease_landlord_offer: values.lease_landlord_offer || null,
    owner_is_main_trainer: !!values.owner_is_main_trainer,
    // Equipment
    equipment_original_cost: Number(values.equipment_original_cost || 0),
    equipment_purchase_date: values.equipment_purchase_date || null,
    equipment_level: values.equipment_level || "standard",
    equipment_condition: values.equipment_condition || "average",
    expected_equipment_capex_2_years: Number(values.expected_equipment_capex_2_years || 0),
  });

  const stepValid = useMemo(() => {
    const v = values;
    switch (step) {
      case 0: return !!v.gym_type;
      case 1: return v.active_members !== "" && Number(v.active_members) >= 0 && v.member_change !== "";
      case 2: return v.revenue !== "" && Number(v.revenue) > 0 && v.net_profit !== "";
      case 3: return v.owner_payments !== "" && v.owner_hours !== "" && !!v.working_owners;
      case 4: return v.employees !== "" && !!v.manager_included && !!v.operates_without_owner_4wk;
      case 5: return !!v.financial_records && !!v.lease_tenure && (
        v.lease_tenure !== "under_1_or_uncertain" || !!v.lease_landlord_offer
      );
      case 6: return (
        v.equipment_original_cost !== "" && Number(v.equipment_original_cost) >= 0 &&
        !!v.equipment_purchase_date && !!v.equipment_level && !!v.equipment_condition &&
        v.expected_equipment_capex_2_years !== ""
      );
      case 7: return (
        lead.first_name && lead.last_name && lead.email && lead.phone &&
        lead.business_name && lead.state && lead.selling_timeframe
      );
      default: return true;
    }
  }, [step, values, lead]);

  const loadPreview = async () => {
    try {
      const res = await api.post("/valuation/preview", { inputs: parseInputs() });
      setPreview(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const goNext = async () => {
    if (!stepValid) {
      toast.error("Please complete the required fields.");
      return;
    }
    if (step === 6) {
      await loadPreview();
    }
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    }
  };

  const goBack = () => step > 0 && setStep(step - 1);

  const submit = async () => {
    if (!stepValid) return;
    setSubmitting(true);
    try {
      const res = await api.post("/valuation/finalize", {
        inputs: parseInputs(),
        lead,
      });
      toast.success("Valuation complete — your PDF report is on its way.");
      nav(`/results/${res.data.id}`, { state: { data: res.data } });
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-3xl px-6 py-10 sm:py-16">
        {/* Progress */}
        <div className="mb-8">
          <div className="mb-2 flex items-center justify-between text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
            <span data-testid="step-label">Step {step + 1} of {STEPS.length} — {STEPS[step]}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" data-testid="progress-bar" />
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.25 }}
            >
              {step === 0 && <StepGymType v={values} setV={setV} />}
              {step === 1 && <StepMembers v={values} setV={setV} />}
              {step === 2 && <StepRevenue v={values} setV={setV} />}
              {step === 3 && <StepOwner v={values} setV={setV} />}
              {step === 4 && <StepTeam v={values} setV={setV} />}
              {step === 5 && <StepQuality v={values} setV={setV} />}
              {step === 6 && <StepEquipment v={values} setV={setV} />}
              {step === 7 && (
                <StepLead
                  lead={lead}
                  setL={setL}
                  preview={preview}
                />
              )}
            </motion.div>
          </AnimatePresence>

          <div className="mt-10 flex items-center justify-between">
            <button
              type="button"
              onClick={goBack}
              disabled={step === 0}
              data-testid="back-btn"
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-slate-500 hover:text-slate-800 disabled:opacity-30"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            {step < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={goNext}
                data-testid="next-btn"
                className="btn-primary"
              >
                Continue <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={submit}
                disabled={submitting || !stepValid}
                data-testid="submit-btn"
                className="btn-primary disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Generating…
                  </>
                ) : (
                  <>
                    Show my full report <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

/* ---------------- Step components ---------------- */

function StepGymType({ v, setV }) {
  return (
    <div>
      <h2 className="font-display text-2xl font-bold text-slate-900">Which model best describes the business?</h2>
      <p className="mt-2 text-sm text-slate-600">
        We&apos;ll use this to compare your gym with similar businesses.
      </p>
      <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {GYM_TYPES.map((g) => {
          const active = v.gym_type === g.value;
          return (
            <button
              key={g.value}
              type="button"
              data-testid={`gym-type-${g.value}`}
              onClick={() => setV("gym_type", g.value)}
              className={`rounded-xl border p-4 text-left text-sm font-medium transition-colors ${
                active
                  ? "border-orange-500 bg-orange-50 text-orange-900"
                  : "border-slate-200 hover:border-slate-300"
              }`}
            >
              {g.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepMembers({ v, setV }) {
  const cur = Number(v.active_members || 0);
  const chg = Number(v.member_change || 0);
  const prior = cur - chg;
  const showGrowth = prior > 0;
  const growthPct = showGrowth ? ((chg / prior) * 100).toFixed(1) : null;

  return (
    <div>
      <h2 className="font-display text-2xl font-bold text-slate-900">Members and membership movement</h2>
      <div className="mt-6 space-y-6">
        <div>
          <LabelWithTip
            label="How many active paying members does the gym currently have?"
            testid="tip-members"
            tip="Only count currently paying members. Exclude free members, cancelled, long-term suspended and trial members."
          />
          <Input
            type="number"
            min="0"
            value={v.active_members}
            onChange={(e) => setV("active_members", e.target.value)}
            data-testid="input-active-members"
            className="h-12"
            placeholder="e.g. 420"
          />
        </div>
        <div>
          <LabelWithTip
            label="Over the last 12 months, by how many members has total membership increased or decreased?"
            testid="tip-change"
            tip="Enter a positive number for growth (e.g. 80) or a negative number for a decline (e.g. -50)."
          />
          <Input
            type="number"
            value={v.member_change}
            onChange={(e) => setV("member_change", e.target.value)}
            data-testid="input-member-change"
            className="h-12"
            placeholder="e.g. 35 or -50"
          />
          {showGrowth && (
            <p className="mt-2 text-xs text-slate-500">
              Members 12 months ago: <span className="font-semibold text-slate-800">{prior.toLocaleString()}</span>
              &nbsp;•&nbsp;Annual growth rate:&nbsp;
              <span className={`font-semibold ${Number(growthPct) >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                {growthPct}%
              </span>
            </p>
          )}
          {v.active_members !== "" && v.member_change !== "" && prior <= 0 && (
            <p className="mt-2 text-xs text-rose-600">
              This would produce a zero or negative prior member count — please review.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function StepRevenue({ v, setV }) {
  return (
    <div>
      <h2 className="font-display text-2xl font-bold text-slate-900">Revenue and profit</h2>
      <div className="mt-6 space-y-6">
        <div>
          <LabelWithTip
            label="What was the gym's total revenue over the last 12 months?"
            testid="tip-revenue"
            tip="Include membership fees, PT, group classes, merchandise, supplements, casual visits and any other gym-related revenue."
          />
          <div className="relative">
            <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">$</span>
            <Input
              type="number"
              min="0"
              value={v.revenue}
              onChange={(e) => setV("revenue", e.target.value)}
              data-testid="input-revenue"
              className="h-12 pl-8"
              placeholder="e.g. 520000"
            />
          </div>
        </div>
        <div>
          <LabelWithTip
            label="What was the net profit of the business over the last 12 months?"
            testid="tip-profit"
            tip="Net profit AFTER all ordinary operating expenses, but do NOT deduct income tax, interest, depreciation, amortisation, owner salary or drawings."
          />
          <div className="relative">
            <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">$</span>
            <Input
              type="number"
              value={v.net_profit}
              onChange={(e) => setV("net_profit", e.target.value)}
              data-testid="input-net-profit"
              className="h-12 pl-8"
              placeholder="e.g. 65000"
            />
          </div>
        </div>
        <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-orange-700">About SDE</div>
          <p className="mt-2 text-sm leading-relaxed text-slate-700">
            We use <strong>SDE, Seller Discretionary Earnings</strong>, to help
            value your business. SDE is the real annual earnings of a business
            owner, including salary and other benefits taken from the business.
            It&apos;s commonly used to value small businesses and show how much
            income a buyer could expect if they took over.
          </p>
        </div>
      </div>
    </div>
  );
}

function StepOwner({ v, setV }) {
  const wo = Number(v.working_owners || 1);
  return (
    <div>
      <h2 className="font-display text-2xl font-bold text-slate-900">Owner involvement</h2>
      <div className="mt-6 space-y-6">
        <div>
          <LabelWithTip
            label="Owner payments (wages, salary, super, management fees) — last 12 months"
            testid="tip-owner-pay"
            tip="Total amount taken out of the business by the working owner(s) in the last 12 months. Include wages, salary, superannuation, and management fees."
          />
          <div className="relative">
            <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">$</span>
            <Input
              type="number"
              min="0"
              value={v.owner_payments}
              onChange={(e) => setV("owner_payments", e.target.value)}
              data-testid="input-owner-payments"
              className="h-12 pl-8"
              placeholder="e.g. 70000"
            />
          </div>
        </div>
        <div>
          <LabelWithTip
            label="How many hours per week does the owner normally work in the business?"
            testid="tip-owner-hours"
            tip="Include time spent on training, admin, sales, cleaning and management."
          />
          <Input
            type="number"
            min="0"
            max="100"
            value={v.owner_hours}
            onChange={(e) => setV("owner_hours", e.target.value)}
            data-testid="input-owner-hours"
            className="h-12"
            placeholder="e.g. 38"
          />
        </div>
        <div>
          <Label className="mb-2 block font-medium text-slate-800">How many working owners are included in the profit figure?</Label>
          <div className="grid grid-cols-3 gap-3">
            {WORKING_OWNERS.map((o) => (
              <button
                type="button"
                key={o.value}
                data-testid={`working-owners-${o.value}`}
                onClick={() => setV("working_owners", o.value)}
                className={`rounded-xl border p-3 text-sm font-medium ${
                  wo === o.value
                    ? "border-orange-500 bg-orange-50 text-orange-900"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
        {wo > 1 && (
          <div>
            <LabelWithTip
              label="Combined weekly hours worked by additional owners (excluding the primary owner)"
              testid="tip-additional-hours"
              tip="A full-time equivalent is 38 hours per week. If two additional owners each work 38 hours, enter 76."
            />
            <Input
              type="number"
              min="0"
              value={v.additional_owner_hours}
              onChange={(e) => setV("additional_owner_hours", e.target.value)}
              data-testid="input-additional-owner-hours"
              className="h-12"
              placeholder="e.g. 38"
            />
          </div>
        )}
        <label className="flex items-start gap-3 text-sm text-slate-700">
          <Checkbox
            checked={!!v.owner_is_main_trainer}
            onCheckedChange={(c) => setV("owner_is_main_trainer", !!c)}
            data-testid="owner-main-trainer-check"
          />
          <span>
            The owner is the main trainer, instructor or source of member relationships.
          </span>
        </label>
      </div>
    </div>
  );
}

function StepTeam({ v, setV }) {
  return (
    <div>
      <h2 className="font-display text-2xl font-bold text-slate-900">Employees and management</h2>
      <div className="mt-6 space-y-6">
        <div>
          <Label className="mb-2 block font-medium text-slate-800">How many employees work in the business (excluding owners)?</Label>
          <Input
            type="number"
            min="0"
            value={v.employees}
            onChange={(e) => setV("employees", e.target.value)}
            data-testid="input-employees"
            className="h-12"
            placeholder="e.g. 5"
          />
        </div>
        <div>
          <Label className="mb-2 block font-medium text-slate-800">Is a manager already employed and included in the business expenses?</Label>
          <Select value={v.manager_included} onValueChange={(x) => setV("manager_included", x)}>
            <SelectTrigger className="h-12" data-testid="select-manager-included">
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              {MANAGER_INCLUDED.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`manager-opt-${o.value}`}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-2 block font-medium text-slate-800">Could the business operate normally for four weeks without the owner?</Label>
          <div className="grid grid-cols-3 gap-3">
            {FOUR_WEEK_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                data-testid={`fourweek-${o.value}`}
                onClick={() => setV("operates_without_owner_4wk", o.value)}
                className={`rounded-xl border p-3 text-sm font-medium ${
                  v.operates_without_owner_4wk === o.value
                    ? "border-orange-500 bg-orange-50 text-orange-900"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StepQuality({ v, setV }) {
  return (
    <div>
      <h2 className="font-display text-2xl font-bold text-slate-900">Business quality factors</h2>
      <div className="mt-6 space-y-6">
        <div>
          <Label className="mb-2 block font-medium text-slate-800">How reliable are the financial records?</Label>
          <Select value={v.financial_records} onValueChange={(x) => setV("financial_records", x)}>
            <SelectTrigger className="h-12" data-testid="select-financial-records">
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              {FINANCIAL_RECORDS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-2 block font-medium text-slate-800">How much secure lease tenure remains, including available options?</Label>
          <Select value={v.lease_tenure} onValueChange={(x) => { setV("lease_tenure", x); if (x !== "under_1_or_uncertain") setV("lease_landlord_offer", ""); }}>
            <SelectTrigger className="h-12" data-testid="select-lease-tenure">
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              {LEASE_TENURE.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {v.lease_tenure === "under_1_or_uncertain" && (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
              <Label className="mb-3 block font-medium text-slate-800">
                As you have a shorter lease, do you believe your landlord would offer a new lease to a buyer?
              </Label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: "yes", label: "Yes" },
                  { value: "no", label: "No" },
                ].map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    data-testid={`lease-landlord-${o.value}`}
                    onClick={() => setV("lease_landlord_offer", o.value)}
                    className={`rounded-xl border p-3 text-sm font-medium ${
                      v.lease_landlord_offer === o.value
                        ? "border-orange-500 bg-orange-100 text-orange-900"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              {v.lease_landlord_offer === "no" && (
                <p className="mt-3 text-xs text-rose-700">
                  Without an ongoing lease, the sale of the business carries
                  significant risk — buyers will discount their offers accordingly
                  and some may be unwilling to proceed.
                </p>
              )}
              {v.lease_landlord_offer === "yes" && (
                <p className="mt-3 text-xs text-emerald-700">
                  Great — we&apos;ll treat this as a one-to-three year lease for
                  valuation purposes, subject to a new lease being agreed on
                  reasonable terms.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StepEquipment({ v, setV }) {
  return (
    <div>
      <h2 className="font-display text-2xl font-bold text-slate-900">Equipment</h2>
      <p className="mt-2 text-sm text-slate-600">
        We use these details to estimate the current market value of the gym
        equipment using reducing-balance depreciation over a 10-year useful life.
      </p>
      <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
        The valuation assumes that all equipment included in the sale will
        transfer to the purchaser free of finance, lease liabilities and
        security interests. Any payout required to discharge an equipment lease
        or loan is paid by the vendor at settlement and does not reduce the
        stated valuation.
      </div>
      <div className="mt-6 space-y-6">
        <div>
          <LabelWithTip
            label="Approximately how much was originally spent on the gym equipment currently used in the business?"
            testid="tip-equipment-cost"
            tip="Include cardio, strength machines, racks and rigs, free weights, benches, functional-training equipment, Pilates reformers and studio equipment. Exclude building improvements, flooring, furniture, computers, signage, disposed items and leased equipment not owned by the business."
          />
          <div className="relative">
            <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">$</span>
            <Input
              type="number"
              min="0"
              value={v.equipment_original_cost}
              onChange={(e) => setV("equipment_original_cost", e.target.value)}
              data-testid="input-equipment-cost"
              className="h-12 pl-8"
              placeholder="e.g. 420000"
            />
          </div>
        </div>
        <div>
          <LabelWithTip
            label="When was most of this equipment purchased?"
            testid="tip-equipment-date"
            tip="Enter the month and year most of the equipment was purchased. If the equipment was acquired over several stages, use the average purchase date."
          />
          <Input
            type="month"
            value={v.equipment_purchase_date}
            onChange={(e) => setV("equipment_purchase_date", e.target.value)}
            data-testid="input-equipment-date"
            className="h-12"
            max={new Date().toISOString().slice(0, 7)}
          />
        </div>
        <div>
          <Label className="mb-2 block font-medium text-slate-800">Which level best describes the majority of the equipment?</Label>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {EQUIPMENT_LEVELS.map((lvl) => {
              const active = v.equipment_level === lvl.value;
              return (
                <button
                  key={lvl.value}
                  type="button"
                  data-testid={`equipment-level-${lvl.value}`}
                  onClick={() => setV("equipment_level", lvl.value)}
                  className={`rounded-xl border p-4 text-left transition-colors ${
                    active
                      ? "border-orange-500 bg-orange-50 text-orange-900"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="text-sm font-semibold">{lvl.label}</div>
                  <p className="mt-1 text-xs text-slate-600">{lvl.description}</p>
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <Label className="mb-2 block font-medium text-slate-800">What is the overall condition of the equipment?</Label>
          <Select value={v.equipment_condition} onValueChange={(x) => setV("equipment_condition", x)}>
            <SelectTrigger className="h-12" data-testid="select-equipment-condition">
              <SelectValue placeholder="Select condition" />
            </SelectTrigger>
            <SelectContent>
              {EQUIPMENT_CONDITIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`equipment-condition-${o.value}`}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <LabelWithTip
            label="How much do you expect will need to be spent replacing or refurbishing equipment during the next two years?"
            testid="tip-equipment-capex"
            tip="Estimate the total dollar amount you expect to spend on equipment refurbishment or replacement over the next two years. Enter 0 if no material spend is expected."
          />
          <div className="relative">
            <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">$</span>
            <Input
              type="number"
              min="0"
              value={v.expected_equipment_capex_2_years}
              onChange={(e) => setV("expected_equipment_capex_2_years", e.target.value)}
              data-testid="input-equipment-capex"
              className="h-12 pl-8"
              placeholder="e.g. 15000"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function StepLead({ lead, setL, preview }) {
  return (
    <div>
      <h2 className="font-display text-2xl font-bold text-slate-900">Almost done — where should we send your report?</h2>
      {preview && preview.valuable_by_earnings && (
        <div className="mt-4 rounded-xl border border-orange-200 bg-orange-50 p-4">
          <div className="overline text-orange-700">Preliminary range</div>
          <div className="mt-1 font-display text-2xl font-bold text-slate-900" data-testid="preview-range">
            {currency(preview.low_value)} – {currency(preview.high_value)}
          </div>
          <div className="text-xs text-slate-600">
            Central estimate {currency(preview.central_value)}. Enter your details below to see the full breakdown, benchmarks and saleability score.
          </div>
        </div>
      )}
      {preview && !preview.valuable_by_earnings && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          {preview.message}
        </div>
      )}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Label className="mb-2 block">First name</Label>
          <Input value={lead.first_name} onChange={(e) => setL("first_name", e.target.value)} data-testid="lead-first-name" className="h-12" />
        </div>
        <div>
          <Label className="mb-2 block">Last name</Label>
          <Input value={lead.last_name} onChange={(e) => setL("last_name", e.target.value)} data-testid="lead-last-name" className="h-12" />
        </div>
        <div>
          <Label className="mb-2 block">Email</Label>
          <Input type="email" value={lead.email} onChange={(e) => setL("email", e.target.value)} data-testid="lead-email" className="h-12" />
        </div>
        <div>
          <Label className="mb-2 block">Phone</Label>
          <Input value={lead.phone} onChange={(e) => setL("phone", e.target.value)} data-testid="lead-phone" className="h-12" />
        </div>
        <div className="sm:col-span-2">
          <Label className="mb-2 block">Business name</Label>
          <Input value={lead.business_name} onChange={(e) => setL("business_name", e.target.value)} data-testid="lead-business" className="h-12" />
        </div>
        <div>
          <Label className="mb-2 block">State</Label>
          <Select value={lead.state} onValueChange={(x) => setL("state", x)}>
            <SelectTrigger className="h-12" data-testid="lead-state">
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent>
              {STATES.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-2 block">Are you considering selling?</Label>
          <Select value={lead.selling_timeframe} onValueChange={(x) => setL("selling_timeframe", x)}>
            <SelectTrigger className="h-12" data-testid="lead-timeframe">
              <SelectValue placeholder="Select timeframe" />
            </SelectTrigger>
            <SelectContent>
              {SELLING_TIMEFRAMES.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <label className="mt-6 block text-xs text-slate-500">
        By continuing you agree to be contacted by GymSales.com.au and Bonza
        Business &amp; Franchise Sales about this valuation and related services.
        Your details are stored securely and never sold.
      </label>
    </div>
  );
}
